import Link from "next/link";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@tillix/ui-atoms/atoms/breadcrumb";
import { Button } from "@tillix/ui-atoms/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@tillix/ui-atoms/dropdown-menu";
import { cn } from "@tillix/ui-atoms/lib/utils";
import { ChevronDownIcon } from "lucide-react";
import { getRoute } from "@/features/route/utils";

const USE_LINK_FOR_TITLE = false;

export interface RouteBreadcrumbProps {
  includeHomeLink?: boolean;
  linkClassName?: string;
  separatorClassName?: string;
  t?: (key: string) => string;
}

export default function RouteBreadcrumb({
  url,
  includeHomeLink = false,
  linkClassName,
  separatorClassName,
  t,
}: RouteBreadcrumbProps & { url: URL }) {
  const route = getRoute(url);
  if (!route?.title) return null;

  const activeChild =
    route.children?.find((child) => url.pathname.startsWith(child.path)) ??
    route.children?.[0];

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {includeHomeLink && (
          <>
            <BreadcrumbItem>
              <BreadcrumbLink
                className={cn(
                  "font-semibold text-primary sm:text-xl",
                  linkClassName,
                )}
                href="/"
              >
                {t?.("breadcrumb.home") ?? "Home"}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className={cn(separatorClassName)} />
          </>
        )}
        <BreadcrumbItem>
          {USE_LINK_FOR_TITLE ? (
            <BreadcrumbLink
              className="font-semibold text-primary sm:text-xl"
              href={route.path}
            >
              {t?.(route.title) ?? route.title}
            </BreadcrumbLink>
          ) : (
            <BreadcrumbPage
              className={cn(
                "font-semibold hover:text-primary sm:text-xl",
                linkClassName,
              )}
            >
              {t?.(route.title) ?? route.title}
            </BreadcrumbPage>
          )}
        </BreadcrumbItem>
        {!!route.children?.[0] && (
          <>
            <BreadcrumbSeparator className="sm:[&>svg]:size-5" />

            <BreadcrumbItem>
              <DropdownMenu>
                <DropdownMenuTrigger className="group">
                  <Button
                    className="-mx-4 -my-2 flex items-center gap-1 font-normal hover:bg-transparent sm:text-xl"
                    variant="ghost"
                  >
                    {activeChild?.title ? (
                      (t?.(activeChild.title) ?? activeChild.title)
                    ) : (
                      <span>
                        {t?.("breadcrumb.unknownPage") ?? "Unknown Page"}
                      </span>
                    )}
                    <span> </span>
                    <span>
                      {route.children.length > 1 && (
                        <ChevronDownIcon className="size-4 transition-transform group-data-[state=open]:rotate-180" />
                      )}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  {route.children.map((child) => (
                    <DropdownMenuItem
                      className={cn("cursor-pointer", {
                        "font-medium": url.pathname === child.path,
                      })}
                      disabled={url.pathname === child.path}
                      key={child.path}
                    >
                      <Link href={child.path}>
                        {child.title
                          ? (t?.(child.title as string) ?? child.title)
                          : null}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </BreadcrumbItem>
          </>
        )}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
